import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

interface PromoCode {
  id: string;
  code: string;
  discount: number;
  lifetime: boolean;
  users: number;
  createdAt: Date | null;
  lastUsed: Date | null;
}

const AdminPromoCodesPage: React.FC = () => {
  const navigate = useNavigate();
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState<number>(20);
  const [lifetime, setLifetime] = useState<boolean>(true);

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      setError(null);

      const listPromoCodesFunction = httpsCallable(functions, 'listPromoCodes');
      const result = await listPromoCodesFunction({});
      const data = result.data as any;

      if (data?.success && data.promoCodes) {
        const parseDate = (d: any) => (d ? new Date(d) : null);
        const promoCodesWithDates = data.promoCodes.map((p: any) => ({
          ...p,
          createdAt: parseDate(p.createdAt),
          lastUsed: parseDate(p.lastUsed),
        }));
        setPromoCodes(promoCodesWithDates);
      } else {
        setError(data?.error || 'Failed to load promo codes');
      }
    } catch (err: any) {
      console.error('Error loading promo codes:', err);
      setError(err.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePromoCode = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      setError(null);
      setSuccess(null);

      const createPromoCodeFunction = httpsCallable(functions, 'createPromoCode');
      const result = await createPromoCodeFunction({
        code,
        discount,
        lifetime,
      });
      const data = result.data as any;

      if (data?.success) {
        setSuccess('Promo code created successfully!');
        await loadPromoCodes();
        setCode('');
      } else {
        setError(data?.error || 'Failed to create promo code');
      }
    } catch (err: any) {
      console.error('Error creating promo code:', err);
      setError(err.message || 'Failed to create promo code');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#262626]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500 mb-4"></div>
          <p className="text-white text-lg">Loading promo codes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center px-4 pt-10 pb-12 bg-[#262626]">
      <div className="w-11/12 md:max-w-4xl mb-4 flex justify-between">
        <button
          onClick={() => navigate('/admindashboard')}
          className="text-sm text-gray-400 hover:text-white transition-colors"
        >
          ← Admin Dashboard
        </button>
      </div>

      <div
        className="mx-auto w-11/12 md:max-w-2xl rounded-[60px] py-8 px-10 mb-10"
        style={{
          backgroundColor: '#1e1e1e',
          boxShadow: '0px 0px 20px 0px rgba(0, 0, 0, 0.75)',
        }}
      >
        <h1 className="mb-6 text-center text-xl text-gray-500 uppercase font-bold">
          Create Promo Code
        </h1>

        <form onSubmit={handleCreatePromoCode} className="space-y-5">
          <div>
            <label className="text-gray-300 block mb-2" htmlFor="code">
              Promo Code
            </label>
            <input
              id="code"
              className="w-full rounded border border-gray-700 bg-white p-2.5 text-black outline-none focus:ring-2 focus:ring-emerald-500 font-mono uppercase"
              type="text"
              placeholder="e.g. DISCOUNT20"
              value={code}
              onChange={e => setCode(e.target.value)}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-gray-300 block mb-2" htmlFor="discount">
                Discount (%)
              </label>
              <input
                id="discount"
                className="w-full rounded border border-gray-700 bg-white p-2.5 text-black outline-none focus:ring-2 focus:ring-emerald-500"
                type="number"
                min="1"
                max="100"
                value={discount}
                onChange={e => setDiscount(Number(e.target.value))}
                required
              />
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center space-x-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={lifetime}
                  onChange={e => setLifetime(e.target.checked)}
                  className="w-5 h-5 accent-emerald-500 rounded border-gray-700"
                />
                <span className="text-gray-300">Lifetime deal?</span>
              </label>
            </div>
          </div>

          {error && <p className="text-red-500 text-sm text-center">{error}</p>}
          {success && <p className="text-emerald-400 text-sm text-center">{success}</p>}

          <button
            type="submit"
            disabled={creating}
            className="w-full disabled:opacity-30 uppercase bg-[#262626] cursor-pointer rounded border border-gray-600 p-2.5 text-base text-white outline-none transition-all duration-300 hover:scale-[1.02] active:scale-95"
          >
            {creating ? 'Creating...' : 'Generate Code'}
          </button>
        </form>
      </div>

      <div className="w-11/12 md:max-w-5xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Active Promo Codes</h2>
          <span className="px-3 py-1 bg-gray-700 text-white rounded-full text-sm font-semibold">
            {promoCodes.length}
          </span>
        </div>

        {promoCodes.length === 0 ? (
          <div className="text-center py-12 bg-[#1e1e1e] rounded-[30px] border border-gray-800">
            <p className="text-gray-400">No promo codes found</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[30px] shadow-2xl border border-gray-800 overflow-hidden">
            <table className="min-w-full bg-[#1e1e1e]">
              <thead>
                <tr className="bg-gray-800/50 border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider text-center">
                    Discount
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider text-center">
                    Lifetime
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider text-center">
                    Users
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {promoCodes.map(promo => (
                  <tr key={promo.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono font-bold text-emerald-400">
                      {promo.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-center">
                      <span className="font-bold">{promo.discount}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      {promo.lifetime ? (
                        <span className="bg-blue-500/10 text-blue-400 border border-blue-500/20 px-2 py-0.5 rounded text-xs">
                          Lifetime
                        </span>
                      ) : (
                        <span className="bg-gray-700 text-gray-400 px-2 py-0.5 rounded text-xs text-nowrap">
                          Recurring
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white text-center">
                      <span className="bg-gray-700 px-3 py-1 rounded-full font-bold">
                        {promo.users}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono">
                      {promo.lastUsed ? promo.lastUsed.toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 font-mono">
                      {promo.createdAt ? promo.createdAt.toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromoCodesPage;
